import PropTypes from 'prop-types'
import styled from 'styled-components'

import isEmpty from 'lodash/isEmpty'

import colors from 'components/utils/colors'
import Icon from 'components/Shared/Icon/Index'
import IconLink from 'components/Shared/IconLink/Index'
import Badge from 'components/Shared/Badge/Index'

import Filter from './Filter'
import MultipleFilters from './MultipleFilters'
import OtherItems from './OtherItems'

const StyledWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.secondary20};
  gap: 8px;
`

const StyledTitle = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${colors.secondary40};
`

const DeleteIcon = styled(IconLink)`
  padding: 2px;
  border-radius: 50%;

  &:before {
    color: ${colors.secondary70} !important;
  }

  &:hover:before {
    color: red !important;
  }
`

const StyledFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MixedFiltersField = ({
  filterList, multipleFilterList, searchValue, maxDisplayItems, onClear, onClearTooltip,
  tooltipText, tooltipId, onContractorClick, title, hideIcon, providerTicketBoardParams,
}) => {
  const hasFilters = Boolean(multipleFilterList?.length > 0 || filterList?.length > 0)
  const hasSearch = Boolean(searchValue?.value)
  const hasProviderTicketBoard = !isEmpty(providerTicketBoardParams)
  const showTitle = title && hasFilters

  if (!hasFilters && !hasSearch && !hasProviderTicketBoard) {
    return null
  }

  return (
    <StyledWrapper data-test='mixed-filters-field'>
      {!hideIcon && (
        <Icon
          iconName='icon-filtered-16'
          active
          hoverable={false}
          data-tooltip-content={tooltipText}
          data-tooltip-id={tooltipId}
        />
      )}

      {!isEmpty(providerTicketBoardParams) && (
        <>
          <StyledTitle>{providerTicketBoardParams.title}</StyledTitle>
          <Badge color={providerTicketBoardParams.color}>
            {providerTicketBoardParams.name}
          </Badge>
        </>
      )}

      {showTitle && <StyledTitle>{title}</StyledTitle>}

      {multipleFilterList.map(obj => {
        return (
          <MultipleFilters
            key={obj.title}
            title={obj.title}
            dataTest={obj.dataTest}
            items={obj.items}
            onClear={obj.onClear}
            clearTooltip={obj.clearTooltip}
            tooltipId={tooltipId}
            maxDisplayItems={maxDisplayItems}
            onContractorClick={onContractorClick}
          />
        )
      })}

      {filterList?.length > 0 && (
        <StyledFilters data-test='single-filters'>
          {filterList.slice(0, maxDisplayItems).map(filter => {
            return (
              <Filter
                key={filter.title || filter.value}
                title={filter.title}
                value={filter.value}
                color={filter.color}
                clearTooltip={filter.clearTooltip}
                tooltipId={tooltipId}
                onClear={filter.onClear}
              />
            )
          })}

          <OtherItems
            itemsCount={filterList.length}
            onClick={onContractorClick}
            maxDisplayItems={maxDisplayItems}
          />
        </StyledFilters>
      )}

      {searchValue?.value && (
        <>
          <StyledTitle>{searchValue.title}</StyledTitle>
          <Badge>
            {searchValue.value}
          </Badge>
        </>

      )}

      {onClear && (
        <DeleteIcon
          iconName='icon-close-filters-12'
          onClick={onClear}
          data-tooltip-content={onClearTooltip}
          data-tooltip-id={tooltipId}
        />
      )}

    </StyledWrapper>
  )
}

MixedFiltersField.propTypes = {
  providerTicketBoardParams: PropTypes.shape({
    title: PropTypes.string,
    name: PropTypes.string,
    color: PropTypes.string,
  }),
  filterList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      title: PropTypes.string,
      color: PropTypes.string,
      onClear: PropTypes.func,
      clearTooltip: PropTypes.string,
    })
  ),
  multipleFilterList: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      dataTest: PropTypes.string,
      onClear: PropTypes.func,
      clearTooltip: PropTypes.string,
      items: PropTypes.array.isRequired,
    })
  ),
  searchValue: PropTypes.shape({
    value: PropTypes.string,
    title: PropTypes.string,
  }),
  maxDisplayItems: PropTypes.number,
  onClear: PropTypes.func,
  onClearTooltip: PropTypes.string,
  tooltipText: PropTypes.string,
  tooltipId: PropTypes.string,
  onContractorClick: PropTypes.func,
  title: PropTypes.string,
  hideIcon: PropTypes.bool,
}

export default MixedFiltersField
